import { useState, useMemo, useEffect } from "react";
import { Button, Collapse } from "antd";
import { CaretRightOutlined, CloseOutlined } from "@ant-design/icons";
import SearchInput from "@/components/UI/SearchInput";
import {
  hasFilterValue,
  formatFilterLabel,
} from "@/components/UI/TableToolbar/Filtering/filtersUtil";
import FilterDropdown from "@/components/UI/TableToolbar/Filtering/FilterDropdown";
import { normalizedIncludes } from "@/utils/stringUtils";
import InfoTooltip from "@/components/UI/InfoTooltip";
import type { ColumnOption } from "@/components/VivoPlots/types";

type FilterValue = string | number | boolean | null;
type SelectedFilters = Record<string, FilterValue[]>;

const createFilterOptions = (values: (string | boolean | null)[]) => {
  return values.map((option) => ({
    label: formatFilterLabel(option),
    value: option as FilterValue,
  }));
};

const getFilterMetadata = (metadata: any, filterKey: string) => ({
  dataType: metadata?.filters?.[filterKey]?.data_type || "text",
  values: metadata?.filters?.[filterKey]?.values || [],
});

const CollapseExpandIcon = ({ isActive }: { isActive?: boolean }) => (
  <CaretRightOutlined
    style={{
      transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
      transition: "transform 0.3s ease",
      color: "#556C75",
      fontSize: "12px",
    }}
  />
);

type DataTabProps = {
  globalFilters: any;
  metadata: any;
  columnsTitleCreator?: (column: string) => string;
  onFiltersChange?: (filters: SelectedFilters) => void;
  plotFilters?: SelectedFilters;
  columnOptions: ColumnOption[];
};

export default function DataTab({
  globalFilters,
  metadata,
  columnsTitleCreator = (column: string) => column,
  onFiltersChange,
  plotFilters = {},
  columnOptions = [],
}: DataTabProps) {
  const columnOptionNames = useMemo(
    () => columnOptions.map((column) => column.name),
    [columnOptions],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentFilters, setCurrentFilters] =
    useState<SelectedFilters>(plotFilters);

  useEffect(() => {
    setCurrentFilters(plotFilters);
  }, [plotFilters]);

  const availableFilters = useMemo(() => {
    return metadata?.filters || {};
  }, [metadata]);

  const filtersKeys = useMemo(
    () => Object.keys(availableFilters),
    [availableFilters],
  );

  const availableFilterKeys = useMemo(
    () =>
      filtersKeys.filter(
        (key) =>
          !hasFilterValue(currentFilters[key] as any) &&
          normalizedIncludes(columnsTitleCreator(key), searchTerm) &&
          columnOptionNames.includes(key),
      ),
    [
      filtersKeys,
      currentFilters,
      searchTerm,
      columnOptionNames,
      columnsTitleCreator,
    ],
  );

  const filteredActiveFilters = useMemo(() => {
    return Object.entries(currentFilters).filter(
      ([key, values]) =>
        values &&
        values.length > 0 &&
        normalizedIncludes(columnsTitleCreator(key), searchTerm) &&
        columnOptionNames.includes(key),
    );
  }, [currentFilters, searchTerm, columnOptionNames, columnsTitleCreator]);

  const handleClearFilter = (filterKey: string) => {
    const newFilters = { ...currentFilters };
    delete newFilters[filterKey];
    setCurrentFilters(newFilters);

    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const handleFiltersUpdate = (newFilters: SelectedFilters) => {
    setCurrentFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const collapseStyles = {
    header: { padding: "0px" },
    body: { padding: "10px 0px 5px 0px" },
  };

  const hasActiveGlobalFilters = useMemo(() => {
    return Object.entries(globalFilters || {}).some(
      ([, values]) => values && Array.isArray(values) && values.length > 0,
    );
  }, [globalFilters]);

  return (
    <div className="flex flex-col pr-2">
      <SearchInput
        placeholder="Search for filters"
        value={searchTerm}
        onChange={setSearchTerm}
        testId="data-tab-search-input"
      />

      {hasActiveGlobalFilters && (
        <Collapse
          ghost
          expandIcon={({ isActive }) => (
            <CollapseExpandIcon isActive={isActive} />
          )}
          className="mt-2 mb-2"
          size="small"
          items={[
            {
              key: "global-filters",
              label: (
                <span
                  className="text-xs text-[#556C75]"
                  data-testid="active-workspace-filters-section"
                >
                  ACTIVE WORKSPACE FILTERS (
                  {
                    Object.entries(globalFilters || {}).filter(
                      ([, values]) =>
                        values && Array.isArray(values) && values.length > 0,
                    ).length
                  }
                  )
                </span>
              ),
              children: (
                <div className="flex flex-col gap-2">
                  {Object.entries(globalFilters || {})
                    .filter(
                      ([, values]) =>
                        values && Array.isArray(values) && values.length > 0,
                    )
                    .map(([filterKey, values]) => {
                      const { dataType, values: filterValues } =
                        getFilterMetadata(metadata, filterKey);
                      return (
                        <div key={`global-${filterKey}`} className="w-[255px]">
                          <FilterDropdown
                            filterKey={filterKey}
                            dataType={dataType}
                            filterOptions={
                              createFilterOptions(filterValues) as any
                            }
                            setFilters={() => {}}
                            currentFilters={{ [filterKey]: values }}
                            tileWidth="100%"
                            placement="leftTop"
                            showTitle={true}
                            alignOffset={{ offset: [-25, 0] }}
                            columnsTitleCreator={columnsTitleCreator}
                            onDelete={() => {}}
                            hideFooter={true}
                            disabled={true}
                          />
                        </div>
                      );
                    })}
                </div>
              ),
              styles: collapseStyles,
            },
          ]}
        />
      )}

      <Collapse
        ghost
        defaultActiveKey={["plot-filters"]}
        expandIcon={({ isActive }) => (
          <CollapseExpandIcon isActive={isActive} />
        )}
        className="mt-2 mb-2"
        size="small"
        items={[
          {
            key: "plot-filters",
            label: (
              <span
                className="text-xs text-[#556C75]"
                data-testid="active-widget-filters-section"
              >
                ACTIVE WIDGET FILTERS
              </span>
            ),
            children: (
              <>
                {filteredActiveFilters.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {filteredActiveFilters.map(([filterKey]) => {
                      const { dataType, values: filterValues } =
                        getFilterMetadata(metadata, filterKey);
                      return (
                        <div
                          key={filterKey}
                          className="flex items-center gap-2"
                        >
                          <div className="w-[255px]">
                            <FilterDropdown
                              filterKey={filterKey}
                              dataType={dataType}
                              filterOptions={
                                createFilterOptions(filterValues) as any
                              }
                              setFilters={handleFiltersUpdate}
                              currentFilters={currentFilters}
                              tileWidth="100%"
                              placement="leftTop"
                              showTitle={true}
                              alignOffset={{ offset: [-25, 0] }}
                              columnsTitleCreator={columnsTitleCreator}
                              onDelete={() => {}}
                              useAlternativeLayout={true}
                            />
                          </div>
                          <Button
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            data-testid={`active-widget-filter-remove-${filterKey}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleClearFilter(filterKey);
                            }}
                            className="text-[#00000073] p-1 w-6 h-6 flex items-center justify-center shrink-0"
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[#00000073] text-sm">
                    Filter something and it&apos;ll show up here.
                  </p>
                )}
              </>
            ),
            styles: collapseStyles,
          },
        ]}
      />

      <Collapse
        ghost
        defaultActiveKey={["available-filters"]}
        expandIcon={({ isActive }) => (
          <CollapseExpandIcon isActive={isActive} />
        )}
        className="mt-2 mb-2"
        size="small"
        items={[
          {
            key: "available-filters",
            label: (
              <span
                className="text-xs text-[#556C75]"
                data-testid="available-widget-filters-section"
              >
                AVAILABLE WIDGET FILTERS{" "}
                <span className="text-gray-500 text-xs">
                  <InfoTooltip
                    title="Includes shown and hidden filters"
                    fill="#6b7280"
                    iconSize={12}
                  />
                </span>
              </span>
            ),
            children: (
              <div>
                {availableFilterKeys.map((filterKey) => {
                  const { dataType, values: filterValues } = getFilterMetadata(
                    metadata,
                    filterKey,
                  );
                  const filterOptionsMemo = createFilterOptions(filterValues);
                  return (
                    <div className="mb-2" key={filterKey}>
                      <FilterDropdown
                        filterKey={filterKey}
                        dataType={dataType}
                        filterOptions={filterOptionsMemo as any}
                        setFilters={handleFiltersUpdate}
                        currentFilters={currentFilters}
                        tileWidth="100%"
                        placement="leftTop"
                        showTitle={true}
                        alignOffset={{ offset: [-25, 0] }}
                        columnsTitleCreator={columnsTitleCreator}
                        onDelete={() => handleClearFilter(filterKey)}
                        useAlternativeLayout={true}
                        showAsAvailable={true}
                      />
                    </div>
                  );
                })}
              </div>
            ),
            styles: collapseStyles,
          },
        ]}
      />
    </div>
  );
}
